/**
 * Return true only when a JWT carries a finite `exp` claim that is already
 * past. This is a client-side cache freshness check, not authentication:
 * signature and claim validation remain server-owned.
 *
 * Opaque/legacy tokens and malformed JWTs are left for the server to reject so
 * this helper does not silently change their existing behavior.
 */
export function isJwtExpired(token: string, nowMs = Date.now()): boolean {
  const parts = token.split('.');
  if (parts.length !== 3) return false;

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const payload = JSON.parse(globalThis.atob(padded)) as { exp?: unknown };
    return typeof payload.exp === 'number'
      && Number.isFinite(payload.exp)
      && payload.exp * 1000 <= nowMs;
  } catch {
    return false;
  }
}

/**
 * Read the class bound into a locally issued dev JWT.
 *
 * `undefined` means the token is opaque, malformed, or not a dev token and
 * must remain under the server's authority. `null` means it is a dev token
 * without a usable class claim. This is cache-routing metadata only; it does
 * not authenticate the token or replace server-side signature validation.
 */
export function readDevJwtCharacterClass(token: string): string | null | undefined {
  const parts = token.split('.');
  if (parts.length !== 3) return undefined;

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const payload = JSON.parse(globalThis.atob(padded)) as {
      dev?: unknown;
      characterClass?: unknown;
    };
    if (payload.dev !== true) return undefined;
    return typeof payload.characterClass === 'string' && payload.characterClass.trim()
      ? payload.characterClass.trim().toLowerCase()
      : null;
  } catch {
    return undefined;
  }
}

/**
 * True when a JWT carries the `guest: true` claim minted by
 * POST /api/auth/guest/issue. Routing metadata only (which entry flow and
 * which sign-out to use); the server still verifies every token.
 */
export function isGuestJwt(token: string | null | undefined): boolean {
  const parts = typeof token === 'string' ? token.split('.') : [];
  if (parts.length !== 3) return false;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    return (JSON.parse(globalThis.atob(padded)) as { guest?: unknown }).guest === true;
  } catch {
    return false;
  }
}
